import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../api";
import AppLayout from "../components/AppLayout";
import { Empty, ErrorNote, Loading, SectionTitle } from "../components/Feedback";
import RoCrateButton from "../components/RoCrateButton";
import StudyCrateImport from "./StudyCrateImport";
import { DeleteSourceDialog, EditNameForm } from "./SourceEditDelete";
import { Badge, Btn, Card, Field, Input, Select, Textarea } from "../components/Shared";
import { DATASET_KINDS, KIND_HINT } from "../datasetKinds";
import FieldValuesForm from "../forms/FieldValuesForm";
import { useApi } from "../useApi";

export default function StudyPage() {
  const { studyId } = useParams();
  const navigate = useNavigate();
  const study = useApi(`/studies/${studyId}`);
  const datasets = useApi(`/studies/${studyId}/datasets`);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", kind: "primary" });
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);     // Edit study (name, description)
  const [deleting, setDeleting] = useState(false);   // the Delete study dialog
  const s = study.data;

  const create = async e => {
    e.preventDefault();
    setError(null);
    try {
      const d = await api.post(`/studies/${studyId}/datasets`, form);
      navigate(`/datasets/${d.id}`);
    } catch (err) {
      setError(err);
    }
  };

  return (
    <AppLayout
      title={s ? s.name : "Study"}
      crumbs={[{ label: "Projects", to: "/projects" },
               s ? { label: s.project_name, to: `/projects/${s.project_id}` } : { label: "…" },
               { label: s ? s.name : "…" }]}
    >
      <ErrorNote error={study.error} />
      {study.loading && <Loading />}
      {s && (
        <>
          <Card style={{ marginBottom: 24, padding: "20px 24px" }}>
            {s.description && <p style={{ margin: "0 0 10px", fontSize: 14, color: "#374151" }}>{s.description}</p>}
            <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: "#6b7280" }}>
              <span data-testid="study-editors">
                Edited by {s.created_by ? <Badge color="blue">{s.created_by.full_name || s.created_by.username}</Badge> : "its author"}{" "}
                and the project's curators
              </span>
              {!s.can_edit && <span>(read-only for you)</span>}
            </div>
            <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
              <RoCrateButton kind="studies" id={s.id} filename={`study-${s.id}.zip`} />
              {s.can_edit && <StudyCrateImport study={s} onImported={datasets.reload} />}
              {s.can_edit && !editing && <Btn variant="secondary" onClick={() => setEditing(true)}>Edit study</Btn>}
              {s.can_edit && <Btn variant="danger" onClick={() => setDeleting(true)}>Delete study</Btn>}
            </div>
            {editing && (
              <EditNameForm kind="study" path={`/studies/${s.id}`} item={s}
                            onSaved={() => { setEditing(false); study.reload(); }} onCancel={() => setEditing(false)} />
            )}
          </Card>
          {deleting && (
            <DeleteSourceDialog kind="study" path={`/studies/${s.id}`} item={s}
                                onDeleted={() => navigate(`/projects/${s.project_id}`)} onClose={() => setDeleting(false)} />
          )}

          {/* The study's additional fields (N6): the study's editors fill them in. */}
          <FieldValuesForm path={`/studies/${s.id}/field-values`} canEdit={!!s.can_edit}
                           formLabel={`Fields of ${s.name}`} projectId={s.project_id} />

          <SectionTitle right={s.can_edit && !creating && <Btn onClick={() => setCreating(true)}>New dataset</Btn>}>
            Datasets
          </SectionTitle>
          {creating && (
            <Card style={{ marginBottom: 18 }}>
              <form onSubmit={create} aria-label="New dataset" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <ErrorNote error={error} />
                <Field label="Dataset name" required>
                  <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                </Field>
                <Field label="Description">
                  <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                </Field>
                <Field label="Kind" hint={KIND_HINT}>
                  <Select options={DATASET_KINDS} value={form.kind}
                          onChange={e => setForm(f => ({ ...f, kind: e.target.value || "primary" }))} />
                </Field>
                <div style={{ display: "flex", gap: 10 }}>
                  <Btn type="submit" disabled={!form.name}>Create dataset</Btn>
                  <Btn variant="secondary" onClick={() => setCreating(false)}>Cancel</Btn>
                </div>
              </form>
            </Card>
          )}
          <ErrorNote error={datasets.error} />
          {datasets.loading ? <Loading /> : (datasets.data || []).length === 0 ? (
            <Empty>No datasets yet.</Empty>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {datasets.data.map(d => (
                <Card key={d.id} style={{ padding: "16px 22px" }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <Link to={`/datasets/${d.id}`} style={{ fontSize: 16, fontWeight: 600, color: "#1a1a1a" }}>{d.name}</Link>
                    {d.kind && <Badge color={d.kind === "derived" ? "amber" : "gray"}>{d.kind}</Badge>}
                  </div>
                  {d.description && <p style={{ margin: "6px 0 0", fontSize: 14, color: "#374151" }}>{d.description}</p>}
                  <p style={{ margin: "6px 0 0", fontSize: 13, color: "#6b7280" }}>
                    {d.file_count} file{d.file_count === 1 ? "" : "s"}
                  </p>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </AppLayout>
  );
}
